import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Grid from "@mui/material/Grid";
import { formValue } from "@/src/services/formServiceFunctions";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import { getCaseReferenceNumberRegEx, getInsurerNumberRegEx } from "@/src/services/insurerService";
import PersonIcon from "@/src/assets/icons/provision/toggleable/IconPerson";
import FamilyIcon from "@/src/assets/icons/generic/toggleable/IconFamilyDetailed";
import Person from "@/src/components/Form/Common/Person/Person";
import { useForm } from "@/src/provider/Form/FormProvider";
import { caseReferenceNumberIsUnique, isRequired, matchRegexp } from "@/src/services/validationRules";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import Group from "@/src/components/Wizard/Group";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import { getProductData, isInsurerPartnered } from "@/src/services/productService";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import { translate } from "@/src/services/translations/translations";
import NumberFieldConfigured from "@/src/components/Form/ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box } from "@mui/material";
import ValidatorAutocomplete from "@/src/components/FormFields/ValidatorAutocomplete";
import { sortedInsurer } from "@/src/services/insurersList";
import InsurerEntity from "@/src/types/Entities/Insurer";

type InsuranceProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  withDeductible?: boolean;
  insuranceSubArea?: string;
  forceInsuredOptionHidden?: boolean;
};

export default function Insurance({ submitProps, product, withDeductible = false, insuranceSubArea = "", forceInsuredOptionHidden = false }: InsuranceProps) {
  const { values, handleSubmit, registerValidators, initialValues } = useForm();
  const insurerNumberRegex = getInsurerNumberRegEx(values.caseInsurance.insurer);
  const caseReferenceNumberRegex = getCaseReferenceNumberRegEx(values.caseInsurance.insurer);
  const [insuranceNumberRequired, setInsuranceNumberRequired] = useState(true);

  useEffect(() => {
    if (product.caseInsurance.insurancePolicyNumber) {
      // field is hidden if it's already filled
      // a required validator on a hidden field makes submitting the form impossible
      setInsuranceNumberRequired(false);
      return;
    }
    if (product.productCategory === "contractLaw") {
      setInsuranceNumberRequired(true);
      return;
    }
    setInsuranceNumberRequired(values.legalExpenseInsurance === "yes" && !isInsurerPartnered(product));
  }, [values.legalExpenseInsurance]);

  useEffect(() => {
    // if insurer number is not required, clear validators, otherwise use configured validators from field
    if (!insuranceNumberRequired) {
      registerValidators("caseInsurance.insurancePolicyNumber", []);
      return;
    }
  }, [insurerNumberRegex, insuranceNumberRequired]);

  useEffect(() => {
    if (values.legalExpenseInsurance !== "yes") {
      registerValidators("caseInsurance.caseReferenceNumber", [
        {
          validator: caseReferenceNumberIsUnique,
          params: [initialValues.caseInsurance.caseReferenceNumber],
          message:
            "Diese Schadennummer ist bereits einem anderen Rechtsfall zugeordnet. Bitte überprüfen Sie die Schadennummer. Wenn Sie sich nicht sicher sind, lassen Sie das Feld vorerst leer.",
        },
      ]);
      return;
    }

    if (!isInsurerPartnered(product)) {
      registerValidators("caseInsurance.caseReferenceNumber", [
        {
          validator: matchRegexp,
          params: [caseReferenceNumberRegex],
          message: "Ihre Schadens-/Leistungsnummer hat das falsche Format",
        },
        {
          validator: caseReferenceNumberIsUnique,
          params: [initialValues.caseInsurance.caseReferenceNumber],
          message:
            "Diese Schadennummer ist bereits einem anderen Rechtsfall zugeordnet. Bitte überprüfen Sie die Schadennummer. Wenn Sie sich nicht sicher sind, lassen Sie das Feld vorerst leer.",
        },
      ]);
    }
  }, [caseReferenceNumberRegex, values.legalExpenseInsurance]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h5"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Versicherung
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            {!isInsurerPartnered(product) && !forceInsuredOptionHidden && (
              <Group
                type="radio"
                name="legalExpenseInsurance"
                question={`Sind Sie im Bereich ${getProductData(product.name, "productCategory").label} rechtsschutzversichert?`}
                options={[
                  {
                    labelText: translate("legalExpenseInsurance.options.yes.label", values),
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                  {
                    labelText: translate("legalExpenseInsurance.options.no.label", values),
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                ]}
                validators={[
                  {
                    validator: isRequired,
                    message: "Bitte wählen Sie eine der angezeigten Antworten aus",
                  },
                ]}
              />
            )}
            {(values.legalExpenseInsurance === "yes" || isInsurerPartnered(product)) && (
              <>
                {!isInsurerPartnered(product) && (
                  <>
                    <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                      <FormLegend>{translate("caseInsurance.insurer.question", values)}</FormLegend>
                    </Grid>
                    <Grid size={12}>
                      <ValidatorAutocomplete
                        label={translate("caseInsurance.insurer.label", values)}
                        name={"caseInsurance.insurer"}
                        validators={[
                          {
                            validator: isRequired,
                            message: "Bitte geben Sie an, wo Sie rechtsschutzversichert sind.",
                          },
                        ]}
                        helperText={
                          "Bitte wählen Sie Ihren Rechtsschutzversicherer aus." +
                          (insuranceSubArea ? ` Wichtig: Sie benötigen eine Versicherung im Bereich ${insuranceSubArea}.` : "")
                        }
                        options={[
                          { label: "", value: "" },
                          ...sortedInsurer.map((insurer: InsurerEntity) => ({
                            label: insurer.name,
                            value: "/insurers/" + insurer.id,
                          })),
                        ]}
                        valueOptionAttribute="value"
                        noOptionsText="Kein Versicherer gefunden"
                        renderOption={(props, option) => {
                          const { key, ...optionProps } = props;

                          return (
                            <li
                              key={option.value || key}
                              {...optionProps}
                              style={{
                                padding: "0.375rem 1rem",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                              }}
                            >
                              {option.label}
                            </li>
                          );
                        }}
                      />
                    </Grid>
                  </>
                )}
                {(!isInsurerPartnered(product) || !product.caseInsurance.insurancePolicyNumber) && (
                  <>
                    <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                      <FormLegend>{translate("caseInsurance.insurancePolicyNumber.question", values)}</FormLegend>
                    </Grid>
                    <Grid size={12}>
                      <ValidatorTextField
                        name="caseInsurance.insurancePolicyNumber"
                        label={translate("caseInsurance.insurancePolicyNumber.label", values)}
                        helperText="Sie finden diese Nummer z.B. auf Ihrem Versicherungsschein oder Ihren Beitragsrechnungen."
                        validators={[
                          {
                            validator: isRequired,
                            message: "Bitte geben Sie Ihre Versichertennummer an.",
                          },
                          {
                            validator: matchRegexp,
                            params: [insurerNumberRegex],
                            message: "Bitte prüfen Sie die Nummer. Sie finden diese in Ihren Versicherungsunterlagen. Beispiel: 22.6.123.456.8",
                          },
                        ]}
                      />
                    </Grid>
                  </>
                )}
                {!isInsurerPartnered(product) && (
                  <>
                    <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                      <FormLegend>{translate("caseInsurance.caseReferenceNumber.question", values)}</FormLegend>
                    </Grid>
                    <Grid size={12}>
                      <ValidatorTextField
                        name="caseInsurance.caseReferenceNumber"
                        label={translate("caseInsurance.caseReferenceNumber.label", values)}
                        helperText="Dies ist die Nummer, die Sie zu Ihrem aktuellen Anliegen erhalten haben. Tragen Sie hier bitte NICHT Ihre Versichertennummer ein."
                        validators={[
                          {
                            validator: matchRegexp,
                            params: [caseReferenceNumberRegex],
                            message: "Ihre Schadens-/Leistungsnummer hat das falsche Format",
                          },
                          {
                            validator: caseReferenceNumberIsUnique,
                            params: [initialValues.caseInsurance.caseReferenceNumber],
                            message:
                              "Diese Schadennummer ist bereits einem anderen Rechtsfall zugeordnet. Bitte überprüfen Sie die Schadennummer. Wenn Sie sich nicht sicher sind, lassen Sie das Feld vorerst leer.",
                          },
                        ]}
                      />
                    </Grid>
                  </>
                )}
                {withDeductible && <NumberFieldConfigured caseData={values} fieldPath={"caseInsurance.deductible"} fieldType={"money"} numberType={"float"} />}
                <Grid size={12}>
                  <Group
                    type="radio"
                    name="caseInsurance.policyHolderType"
                    question={translate("caseInsurance.policyHolderType.question", values)}
                    options={[
                      {
                        labelText: translate("caseInsurance.policyHolderType.options.me.label", values),
                        labelIcon: <PersonIcon />,
                        labelIconActive: <PersonIcon active />,
                        value: "me",
                      },
                      {
                        labelText: translate("caseInsurance.policyHolderType.options.familyMember.label", values),
                        labelIcon: <FamilyIcon />,
                        labelIconActive: <FamilyIcon active />,
                        value: "familyMember",
                      },
                    ]}
                    validators={[
                      {
                        validator: isRequired,
                        message: "Bitte wählen Sie eine der angezeigten Antworten aus",
                      },
                    ]}
                  />
                </Grid>
                {formValue(values, "caseInsurance.policyHolderType") === "familyMember" && (
                  <Person path={"caseInsurance.policyHolder"} headline={"Wie heißt die Person, auf die die Rechtsschutz-Versicherung läuft?"} />
                )}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
